import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HiOutlineXMark, HiOutlineMagnifyingGlass } from 'react-icons/hi2';
import './ComprasSearchBar.scss';

interface BasePurchase {
  ingredient_purchase_id: number;
  ingredient_name: string | null;
  provider_name: string | null;
  total_neto: number;
  quantity: number;
  unidad_medida_name: string | null;
  timestamp: string;
}

interface ComprasSearchBarProps<T extends BasePurchase> {
  data: T[];
  updateFilteredData: (filteredData: T[]) => void;
  placeholder?: string;
}

interface SuggestionItem<T> {
  purchase: T;
  matchedField: 'ingredient_name' | 'provider_name';
  matchedValue: string;
}

const removeAccents = (str: string): string => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const ComprasSearchBar = <T extends BasePurchase>({ 
  data, 
  updateFilteredData, 
  placeholder = "Buscar compra..." 
}: ComprasSearchBarProps<T>) => {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<SuggestionItem<T>[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter data based on input
  const filterData = useCallback((searchValue: string): T[] => {
    if (searchValue.trim() === '') {
      return data;
    }

    const normalizedInput = removeAccents(searchValue.toLowerCase());
    return data.filter(purchase => {
      const ingredientName = removeAccents((purchase.ingredient_name || '').toLowerCase());
      const providerName = removeAccents((purchase.provider_name || '').toLowerCase());
      return ingredientName.includes(normalizedInput) || providerName.includes(normalizedInput);
    });
  }, [data]);

  // Generate unique suggestions
  const generateSuggestions = useCallback((searchValue: string): SuggestionItem<T>[] => {
    if (searchValue.trim() === '') {
      return [];
    }

    const normalizedInput = removeAccents(searchValue.toLowerCase());
    const matchedItems: SuggestionItem<T>[] = [];
    const seenValues = new Set<string>();

    for (const purchase of data) {
      const ingredientName = removeAccents((purchase.ingredient_name || '').toLowerCase());
      const providerName = removeAccents((purchase.provider_name || '').toLowerCase());

      // Match ingredient name
      if (ingredientName.includes(normalizedInput) && purchase.ingredient_name) {
        const key = `ingredient:${purchase.ingredient_name.toLowerCase()}`;
        if (!seenValues.has(key)) {
          matchedItems.push({
            purchase,
            matchedField: 'ingredient_name',
            matchedValue: purchase.ingredient_name
          });
          seenValues.add(key);
        }
      }

      // Match provider name
      if (providerName.includes(normalizedInput) && purchase.provider_name) {
        const key = `provider:${purchase.provider_name.toLowerCase()}`;
        if (!seenValues.has(key)) {
          matchedItems.push({
            purchase,
            matchedField: 'provider_name',
            matchedValue: purchase.provider_name
          });
          seenValues.add(key);
        }
      }
    }

    return matchedItems.slice(0, 15);
  }, [data]);

  // Handle input change
  useEffect(() => {
    const filteredData = filterData(input);
    updateFilteredData(filteredData);
    
    if (input.trim() !== '') {
      const newSuggestions = generateSuggestions(input);
      setSuggestions(newSuggestions);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setHighlightedIndex(-1);
  }, [input, filterData, generateSuggestions, updateFilteredData]);

  // Reset when data changes
  useEffect(() => {
    if (input.trim() !== '') {
      const filteredData = filterData(input);
      updateFilteredData(filteredData);
    }
  }, [data, filterData, input, updateFilteredData]);

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: SuggestionItem<T>) => {
    setInput(suggestion.matchedValue);
    setShowSuggestions(false);
    
    const normalizedValue = removeAccents(suggestion.matchedValue.toLowerCase());
    const filteredData = data.filter(purchase => {
      const ingredientName = removeAccents((purchase.ingredient_name || '').toLowerCase());
      const providerName = removeAccents((purchase.provider_name || '').toLowerCase());
      return ingredientName.includes(normalizedValue) || providerName.includes(normalizedValue);
    });
    updateFilteredData(filteredData);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[highlightedIndex]);
        } else {
          setShowSuggestions(false);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setInput('');
    setShowSuggestions(false);
    setSuggestions([]);
    updateFilteredData(data);
    inputRef.current?.focus();
  };

  // Close suggestions but keep filter active
  const handleCloseSuggestions = () => {
    setShowSuggestions(false);
  };

  // Highlight matching text in suggestions
  const highlightMatch = (text: string, query: string): React.ReactNode => {
    if (!query.trim()) return text;
    
    const normalizedQuery = removeAccents(query.toLowerCase());
    const normalizedText = removeAccents(text.toLowerCase());
    const startIndex = normalizedText.indexOf(normalizedQuery);
    
    if (startIndex === -1) return text;
    
    const endIndex = startIndex + query.length;
    return (
      <>
        {text.slice(0, startIndex)}
        <mark className="search-highlight">{text.slice(startIndex, endIndex)}</mark>
        {text.slice(endIndex)}
      </>
    );
  };

  return (
    <div className="compras-search-bar-container" ref={containerRef}>
      <div className="search-input-wrapper">
        <HiOutlineMagnifyingGlass className="search-icon" size={16} />
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => input && suggestions.length > 0 && setShowSuggestions(true)}
          autoComplete="off"
        />
        {input && (
          <button
            className="clear-search-button"
            onClick={handleClearSearch}
            type="button"
            aria-label="Limpiar búsqueda"
          >
            <HiOutlineXMark size={16} />
          </button>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="suggestions-dropdown">
          <div className="suggestions-header">
            <span className="suggestions-count">
              {suggestions.length} resultado{suggestions.length !== 1 ? 's' : ''}
            </span>
            <button
              className="close-suggestions-button"
              onClick={handleCloseSuggestions}
              type="button"
              aria-label="Cerrar sugerencias"
            >
              <HiOutlineXMark size={16} />
            </button>
          </div>
          <ul className="suggestions-list">
            {suggestions.map((suggestion, index) => (
              <li
                key={`${suggestion.matchedField}-${suggestion.matchedValue}-${index}`}
                className={`suggestion-item ${index === highlightedIndex ? 'highlighted' : ''}`}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <div className="suggestion-main">
                  <span className="suggestion-name">
                    {highlightMatch(suggestion.matchedValue, input)}
                  </span>
                  <span className={`suggestion-type-badge ${suggestion.matchedField === 'ingredient_name' ? 'badge-product' : 'badge-provider'}`}>
                    {suggestion.matchedField === 'ingredient_name' ? 'Producto' : 'Proveedor'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ComprasSearchBar;
